import { supabase } from '../../lib/supabase';
import { loadStripe } from '@stripe/stripe-js';
import { STRIPE_CONFIG } from './config';
import type { SubscriptionPlan } from './types';

let stripePromise: Promise<any> | null = null;

export const getStripe = () => {
  if (!stripePromise) {
    if (!STRIPE_CONFIG.publishableKey) {
      throw new Error('Stripe publishable key is not configured');
    }
    stripePromise = loadStripe(STRIPE_CONFIG.publishableKey);
  }
  return stripePromise;
};

export async function createStripeCheckoutSession(plan: SubscriptionPlan): Promise<string> {
  const { data: { user } } = await supabase.auth.getUser();
  
  if (!user) {
    throw new Error('Not authenticated');
  }

  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) {
    throw new Error('Not authenticated');
  }

  const response = await fetch('/api/create-payment-intent', {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${session.access_token}`,
      'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      productId: STRIPE_CONFIG.productId,
      userId: user.id,
      mode: 'subscription',
      successUrl: STRIPE_CONFIG.successUrl,
      cancelUrl: STRIPE_CONFIG.cancelUrl,
    })
  });

  if (!response.ok) {
    let errorData;
    try {
      errorData = await response.json();
    } catch (e) {
      throw new Error('Failed to create payment session');
    }
    throw new Error(errorData.error || 'Payment setup failed');
  }

  const data = await response.json();
  if (!data?.sessionId) {
    throw new Error('Invalid response from payment service');
  }
  
  return data.sessionId;
}

export async function redirectToCheckout(plan: SubscriptionPlan) {
  try {
    const stripe = await getStripe();
    if (!stripe) {
      throw new Error('Stripe failed to initialize');
    }

    const sessionId = await createStripeCheckoutSession(plan);
    const { error } = await stripe.redirectToCheckout({ sessionId });

    if (error) {
      throw error;
    }
  } catch (err) {
    console.error('Error redirecting to checkout:', err);
    throw err;
  }
}

export async function createCustomerPortalSession(): Promise<string> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) {
    throw new Error('Not authenticated');
  }

  const response = await fetch('/api/create-portal-session', {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${session.access_token}`,
      'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
      'Content-Type': 'application/json'
    }
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to create portal session');
  }

  const { url } = await response.json();
  return url;
}

export async function redirectToCustomerPortal() {
  try {
    const url = await createCustomerPortalSession();
    window.location.href = url;
  } catch (err) {
    console.error('Error redirecting to customer portal:', err);
    throw err;
  }
}